<script setup>
/**
 * PhoneSearchStep
 *
 * Step 1 of `NewWhatsAppChatFlow`. Presentation-only: all searching,
 * debouncing, and contact/phone-validation logic lives in the parent flow
 * component so this stays a small, easily testable view.
 *
 * Two ways to start a chat, mirroring how WhatsApp itself separates the two:
 *   - search existing contacts by name/phone (existing-contact detection)
 *   - or enter a brand new phone number with a country selector
 */
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

defineProps({
  searchResults: { type: Array, default: () => [] },
  isSearching: { type: Boolean, default: false },
  isCreatingContact: { type: Boolean, default: false },
  phoneError: { type: String, default: '' },
});

defineEmits(['selectContact', 'startNewChat']);

const searchQuery = defineModel('searchQuery', { type: String, default: '' });
const phoneNumber = defineModel('phoneNumber', { type: String, default: '' });

const { t } = useI18n();
const i18nPrefix = 'NEW_WHATSAPP_CHAT.SEARCH_STEP';
</script>

<template>
  <div class="flex flex-col gap-4 px-4 pb-4 overflow-y-auto">
    <div class="flex flex-col gap-2">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t(`${i18nPrefix}.SEARCH_LABEL`) }}
      </label>
      <div class="relative">
        <Icon
          icon="i-lucide-search"
          class="absolute size-3.5 top-2.5 ltr:left-3 rtl:right-3 text-n-slate-10"
        />
        <input
          v-model="searchQuery"
          type="search"
          data-testid="new-whatsapp-chat-search-input"
          :placeholder="t(`${i18nPrefix}.SEARCH_PLACEHOLDER`)"
          class="w-full h-8 py-2 ltr:pl-9 rtl:pr-9 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
        />
      </div>

      <div v-if="isSearching" class="flex justify-center py-2">
        <Spinner />
      </div>
      <ul v-else-if="searchResults.length" class="flex flex-col gap-0.5">
        <li v-for="contact in searchResults" :key="contact.id">
          <button
            type="button"
            class="flex items-center w-full gap-2 p-2 text-left rtl:text-right rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
            @click="$emit('selectContact', contact)"
          >
            <Avatar :name="contact.name" :src="contact.thumbnail" :size="28" />
            <div class="flex flex-col min-w-0">
              <span class="text-sm truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ contact.phoneNumber || contact.email }}
              </span>
            </div>
          </button>
        </li>
      </ul>
      <p
        v-else-if="searchQuery.trim().length >= 2"
        class="pt-1 text-sm text-n-slate-11"
      >
        {{ t(`${i18nPrefix}.NO_RESULTS`) }}
      </p>
    </div>

    <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t(`${i18nPrefix}.NEW_NUMBER_LABEL`) }}
      </label>
      <form class="flex flex-col gap-2" @submit.prevent="$emit('startNewChat')">
        <PhoneNumberInput
          v-model="phoneNumber"
          :placeholder="t(`${i18nPrefix}.PHONE_PLACEHOLDER`)"
        />
        <p v-if="phoneError" class="text-xs text-n-ruby-9">
          {{ phoneError }}
        </p>
        <Button
          type="submit"
          :label="t(`${i18nPrefix}.START_CHAT_BUTTON`)"
          :is-loading="isCreatingContact"
          :disabled="!phoneNumber"
          size="sm"
        />
      </form>
    </div>
  </div>
</template>
